/* eslint-disable @next/next/no-img-element */
import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import { Layout, Seo, PageHero } from '@/components/generals'
import { routes } from '@/lib/routes'

const appFeatures = [
    { icon: '/images/Folder-Icon.svg', text: 'Seamlessly integrate time, finance, and task management in one app.' },
    { icon: '/images/User-Icon.svg', text: 'Gain a comprehensive overview of your expenses for informed decision-making.' },
    { icon: '/images/Table-Icon.svg', text: 'Effortlessly stay organized with an intuitive interface and powerful features.' },
    { icon: '/images/AI-Icon.svg', text: 'Let the AI-powered personal assistant handle mundane tasks for you.' }
]

const FeaturesPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title='Features' description='Everything San Diego does to keep your time, finances, and tasks effortlessly organized.' />

            <PageHero
                badge='New'
                eyebrow='Introducing the Class app'
                eyebrowHref='#Download'
                title={
                    <>
                        Don&apos;t live like 1800&apos;s, it&apos;s <span className='text-gradient'>2024</span> baby!
                    </>
                }
                description='San Diego keeps your work on-brand, on message, and on time. Innovative features make creating and sharing your work feel effortless.'
                showButtons
            />

            <div id='FeatureList' className='section'>
                <div className='container'>
                    <div className='animation-on-page-load'>
                        <div className='w-layout-grid features-page-grid'>
                            <div id='w-node-_683f6423-2343-b727-5633-7065e36f1185-77af58a2' className='grey-card-item-holder'>
                                <div className='card-content-holder bigger-cards'>
                                    <div className='fade-in-on-scroll'>
                                        <div className='text-center'>
                                            <div className='card-title-sm'>Stay on Top of Your Schedule</div>
                                            <p className='card-description'>Easily integrate with all your favorite tools through a flexible API and Zapier integration.</p>
                                        </div>
                                    </div>
                                    <img
                                        src='/images/Top-Schedule.webp'
                                        loading='lazy'
                                        sizes='(max-width: 767px) 85vw, (max-width: 1279px) 46vw, 624px'
                                        srcSet='/images/Top-Schedule-p-500.png 500w, /images/Top-Schedule-p-800.png 800w, /images/Top-Schedule-p-1080.png 1080w, /images/Top-Schedule-p-1600.png 1600w, /images/Top-Schedule.webp 1835w'
                                        alt='San Diego schedule view'
                                        className='card-iphone-image'
                                    />
                                </div>
                            </div>
                            <div id='w-node-_51e55d01-3c69-3038-c7cd-21ca1bd224bf-77af58a2' className='grey-card-item-holder'>
                                <div className='card-content-holder bigger-cards'>
                                    <div className='fade-in-on-scroll'>
                                        <div className='text-center'>
                                            <div className='card-title-sm'>Financial Freedom</div>
                                            <p className='card-description'>Gain control over your expenses and make smarter financial decisions.</p>
                                        </div>
                                    </div>
                                    <div className='card-image-to-corner'>
                                        <img
                                            src='/images/Financial-Freedom.webp'
                                            loading='lazy'
                                            sizes='100vw'
                                            srcSet='/images/Financial-Freedom-p-500.png 500w, /images/Financial-Freedom-p-800.png 800w, /images/Financial-Freedom-p-1080.png 1080w, /images/Financial-Freedom.webp 1383w'
                                            alt='San Diego finance view'
                                            className='card-image-100pct'
                                        />
                                    </div>
                                </div>
                            </div>
                            <div id='w-node-_897ff961-4a2b-567e-4a21-bf49fcd29a77-77af58a2' className='grey-card-item-holder'>
                                <div className='card-content-holder bigger-cards'>
                                    <div className='fade-in-on-scroll'>
                                        <div className='text-center'>
                                            <div className='card-title-sm'>Safeguard Data</div>
                                            <p className='card-description'>Store and access all your important information securely in one place.</p>
                                        </div>
                                    </div>
                                    <div className='card-image-to-corner'>
                                        <img
                                            src='/images/Safeguard.webp'
                                            loading='lazy'
                                            sizes='100vw'
                                            srcSet='/images/Safeguard-p-500.png 500w, /images/Safeguard-p-800.png 800w, /images/Safeguard-p-1080.png 1080w, /images/Safeguard.webp 1380w'
                                            alt='San Diego security view'
                                            className='card-image-100pct'
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='w-layout-grid features-page-grid _2'>
                        <div id='w-node-_78b7323e-030b-5aa6-7734-8f321585dcc2-77af58a2' className='grey-card-item-holder'>
                            <div className='card-content-holder bigger-cards'>
                                <div className='fade-in-on-scroll'>
                                    <div className='card-item-with-text-the-bottom'>
                                        <div className='card-title-sm'>
                                            <span className='white-text'>Capture Ideas on the Go</span>
                                        </div>
                                    </div>
                                </div>
                                <div className='card-image-holder'>
                                    <img
                                        src='/images/Card-ImagePerson.webp'
                                        loading='lazy'
                                        sizes='(max-width: 767px) 95vw, (max-width: 1279px) 39vw, 503px'
                                        srcSet='/images/Card-ImagePerson-p-500.jpg 500w, /images/Card-ImagePerson-p-800.jpg 800w, /images/Card-ImagePerson-p-1080.jpg 1080w, /images/Card-ImagePerson.webp 1380w'
                                        alt='Person using San Diego'
                                        className='card-image-bg'
                                    />
                                    <div className='card-image-holder-overlay' />
                                </div>
                            </div>
                        </div>
                        <div id='w-node-_2abbd720-0bb2-88d3-f9b1-e54f95e89d83-77af58a2' className='grey-card-item-holder'>
                            <div className='card-content-holder bigger-cards'>
                                <div className='fade-in-on-scroll'>
                                    <div className='text-center'>
                                        <div className='card-title-sm'>Smart Reminders</div>
                                        <p className='card-description'>Never miss a deadline with reminders that respect your focus.</p>
                                    </div>
                                </div>
                                <div className='card-image-to-corner'>
                                    <img
                                        src='/images/Reminder.png'
                                        loading='lazy'
                                        sizes='100vw'
                                        srcSet='/images/Reminder-p-500.png 500w, /images/Reminder-p-800.png 800w, /images/Reminder-p-1080.png 1080w, /images/Reminder-p-1600.png 1600w, /images/Reminder.png 1968w'
                                        alt='San Diego reminders'
                                        className='card-image-100pct'
                                    />
                                </div>
                            </div>
                        </div>
                        <div id='w-node-_2f1d4b38-7a36-2a3c-b862-7b209a203be8-77af58a2' className='grey-card-item-holder'>
                            <div className='card-content-holder bigger-cards'>
                                <div className='fade-in-on-scroll'>
                                    <div className='text-center'>
                                        <div className='card-title-sm'>Your Productivity Sidekick</div>
                                        <p className='card-description'>Let our AI handle mundane tasks and provide valuable insights for optimal efficiency.</p>
                                    </div>
                                </div>
                                <div className='card-image-to-corner'>
                                    <img
                                        src='/images/AI.webp'
                                        loading='lazy'
                                        sizes='100vw'
                                        srcSet='/images/AI-p-500.png 500w, /images/AI-p-800.png 800w, /images/AI-p-1080.png 1080w, /images/AI.webp 1380w'
                                        alt='San Diego AI assistant'
                                        className='card-image-100pct'
                                    />
                                </div>
                            </div>
                        </div>
                        <div id='w-node-_068289f4-e438-fbcc-fb63-74d8e36ae71c-77af58a2' className='grey-card-item-holder'>
                            <div className='card-content-holder bigger-cards'>
                                <div className='fade-in-on-scroll'>
                                    <div className='text-center'>
                                        <div className='card-title-sm'>Personalize Your App Experience</div>
                                        <p className='card-description'>Enjoy a sleek interface and reduce eye strain with our customizable Dark Mode.</p>
                                    </div>
                                </div>
                                <div className='card-image-to-corner'>
                                    <img
                                        src='/images/Personalize.webp'
                                        loading='lazy'
                                        sizes='100vw'
                                        srcSet='/images/Personalize-p-500.png 500w, /images/Personalize-p-800.png 800w, /images/Personalize-p-1080.png 1080w, /images/Personalize.webp 1380w'
                                        alt='San Diego personalization'
                                        className='card-image-100pct'
                                    />
                                </div>
                            </div>
                        </div>
                        <div id='w-node-cf622425-eeeb-8f7c-77c7-371e18bd1ec4-77af58a2' className='grey-card-item-holder'>
                            <div className='card-content-holder bigger-cards'>
                                <div className='fade-in-on-scroll'>
                                    <div className='card-item-with-text-the-bottom'>
                                        <div className='card-title-sm'>
                                            <span className='white-text'>Cross-Platform Syncing: Seamlessly Access Your Data</span>
                                        </div>
                                    </div>
                                </div>
                                <div className='card-image-holder'>
                                    <img
                                        src='/images/CardImagePerson2.webp'
                                        loading='lazy'
                                        sizes='(max-width: 767px) 95vw, (max-width: 1279px) 39vw, 503px'
                                        srcSet='/images/CardImagePerson2-p-500.jpg 500w, /images/CardImagePerson2-p-800.jpg 800w, /images/CardImagePerson2-p-1080.jpg 1080w, /images/CardImagePerson2.webp 1968w'
                                        alt='Person syncing San Diego'
                                        className='card-image-bg'
                                    />
                                    <div className='card-image-holder-overlay' />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='section'>
                <div className='container'>
                    <div id='w-node-b262d101-88e2-3de7-f57b-8d4a24389714-77af58a2' className='text-left'>
                        <div className='fade-in-on-scroll'>
                            <h2>
                                Ignite Your Potential with <span className='text-gradient'>Seamless Power</span>
                            </h2>
                        </div>
                        <div className='fade-in-on-scroll'>
                            <p className='grey-text'>One app for time, money, and tasks, so nothing slips through the cracks.</p>
                        </div>
                    </div>
                    <div className='grid-app-features-2cols'>
                        {appFeatures.map(feature => (
                            <div key={feature.text} className='app-features'>
                                <div className='fade-in-on-scroll left'>
                                    <img src={feature.icon} loading='lazy' alt='' className='icon-image' />
                                </div>
                                <div className='fade-in-on-scroll'>
                                    <div className='card-title-sm'>{feature.text}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className='section'>
                <div className='container'>
                    <div id='w-node-_4d156a35-5ff3-7081-9732-1d15bc3c363f-77af58a2' className='text-left'>
                        <div className='fade-in-on-scroll'>
                            <h2 className='text-center'>Empowering You with Advanced Capabilities</h2>
                        </div>
                    </div>
                    <div className='features-page-grid-list'>
                        <div className='features-page-grid _2'>
                            <div className='long-feature-content'>
                                <div className='fade-in-on-scroll'>
                                    <div className='card-title-sm'>Effortless Management</div>
                                </div>
                                <div className='fade-in-on-scroll'>
                                    <div>
                                        Managing your time, finances, and tasks has never been easier than with San Diego. The
                                        user-friendly interface makes navigation effortless, from adding new tasks to editing
                                        existing ones, all streamlined to save you time.
                                    </div>
                                </div>
                                <div className='fade-in-on-scroll'>
                                    <ul role='list' className='list-with-check-icons'>
                                        <li>Add and edit tasks in seconds</li>
                                        <li>Sync across all your devices</li>
                                        <li>See everything in one calm view</li>
                                    </ul>
                                </div>
                                <div className='fade-in-on-scroll'>
                                    <div className='button-holder-left-justify-bottom'>
                                        <Link href={routes.pricing} className='button black-button-with-icon w-button'>
                                            See pricing
                                        </Link>
                                    </div>
                                </div>
                            </div>
                            <div id='w-node-_639c9289-1bcc-e302-dffb-17a6e38c0822-77af58a2' className='grey-card-item-holder card-item-with-xl-bg-image'>
                                <div className='card-content-holder bigger-cards'>
                                    <div className='card-image-holder card-image-effortless-management'>
                                        <img
                                            src='/images/ManagementBg.jpg'
                                            loading='lazy'
                                            sizes='(max-width: 767px) 95vw, (max-width: 1279px) 54vw, 704px'
                                            srcSet='/images/ManagementBg-p-500.jpg 500w, /images/ManagementBg-p-800.jpg 800w, /images/ManagementBg-p-1080.jpg 1080w, /images/ManagementBg-p-1600.jpg 1600w, /images/ManagementBg.jpg 1962w'
                                            alt='San Diego management dashboard'
                                            className='card-image-bg'
                                        />
                                        <div className='card-image-holder-overlay' />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='features-page-grid'>
                            <div className='card-with-person-image features-card-with-person'>
                                <div id='w-node-d6f44763-ad30-c17d-bcb6-29b9132d6280-77af58a2' className='grey-card-item-holder with-top-padding'>
                                    <div className='card-content-holder bigger-cards'>
                                        <div className='fade-in-on-scroll'>
                                            <div className='card-item-with-text-the-bottom' />
                                        </div>
                                        <div className='card-image-holder'>
                                            <img src='/images/Gradient-Box.svg' loading='lazy' alt='' className='card-image-bg' />
                                        </div>
                                    </div>
                                </div>
                                <img
                                    src='/images/Person-Chatting.webp'
                                    loading='lazy'
                                    sizes='(max-width: 767px) 95vw, (max-width: 1279px) 54vw, 704px'
                                    srcSet='/images/Person-Chatting-p-500.png 500w, /images/Person-Chatting-p-800.png 800w, /images/Person-Chatting-p-1080.png 1080w, /images/Person-Chatting-p-1600.png 1600w, /images/Person-Chatting.webp 1782w'
                                    alt='Person chatting with San Diego'
                                    className='card-image-person-chatting'
                                />
                            </div>
                            <div className='card-with-person-image'>
                                <div className='long-feature-content'>
                                    <div className='fade-in-on-scroll'>
                                        <div className='card-title-sm'>
                                            Experience the Excellence with <br />
                                            San Diego
                                        </div>
                                    </div>
                                    <div className='fade-in-on-scroll'>
                                        <div>
                                            From busy professionals balancing work and personal commitments to students striving for
                                            academic success, San Diego has become an indispensable companion. It is the all-in-one
                                            app that brings order, efficiency, and peace of mind to your everyday life.
                                        </div>
                                    </div>
                                    <div className='fade-in-on-scroll'>
                                        <ul role='list' className='list-with-check-icons'>
                                            <li>Built for work and personal life</li>
                                            <li>Smart reminders that respect your focus</li>
                                            <li>Private and secure by default</li>
                                        </ul>
                                    </div>
                                    <div className='fade-in-on-scroll'>
                                        <div className='button-holder-left-justify-bottom'>
                                            <Link href={routes.company} className='button black-button-with-icon w-button'>
                                                About us
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export default FeaturesPage
